import { jsPDF } from "jspdf";
import "svg2pdf.js";
import { buildPrimitives } from "@/lib/geometry";
import { hasPlan, planSketch } from "@/lib/corners";

const download = (blob, name) => {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
};

export async function exportPdf(svgEls, filename, onProgress) {
  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a3", compress: true });
  for (let i = 0; i < svgEls.length; i++) {
    if (i > 0) doc.addPage("a3", "landscape");
    await doc.svg(svgEls[i], { x: 0, y: 0, width: 420, height: 297 });
    onProgress?.(i + 1, svgEls.length);
  }
  doc.save(filename);
}

const LAYER_COLORS = {
  FRAME: 7, SASH: 8, GLASS: 4, SYMBOL: 2, DIM: 3, DIMTEXT: 3, TEXT: 7, LABEL: 9, TITLE: 7, HARDWARE: 2, GLASSNOTE: 5, TAG: 30,
  SHEET: 7, SECTION: 7, HATCH: 8, LEADER: 30, GASKET: 250, SEALANT: 251, PACKER: 9, ALU_PROFILE: 7, GLASS_SPACER: 8, WALL: 8,
  WEATHER_PILE: 6, PU_FOAM: 42, ANCHOR: 1, BACKER_ROD: 6, SETTING_BLOCK: 9, ROLLER: 9, RAILING: 1, LOGO: 7, INFILL: 8,
  INFILL_ACP: 8, INFILL_CEMENT_BOARD: 33, INFILL_PLY: 42, INFILL_ALU_SHEET: 9, INFILL_HPL: 82, INFILL_PUF: 213, INFILL_PANEL: 252,
  IWIN_ALU_PROFILE: 7, IWIN_GASKET: 4, IWIN_DIM: 6, IWIN_HATCH: 8, IWIN_GLASS: 2, IWIN_TITLE: 5, IWIN_RAILING: 1, IWIN_DETAIL: 7,
};

function dxfDoc() {
  const ents = [], layers = new Set(), blocks = [];
  const g = (code, val) => ents.push(String(code), String(val));
  const f = (v) => String(+v.toFixed(3));
  const L = (n) => { const k = String(n || "SHEET").toUpperCase().replace(/[^A-Z0-9_-]/g, "_"); layers.add(k); return k; };
  return {
    line(x1, y1, x2, y2, layer) { g(0, "LINE"); g(8, L(layer)); g(10, f(x1)); g(20, f(y1)); g(30, 0); g(11, f(x2)); g(21, f(y2)); g(31, 0); },
    circle(x, y, r, layer) { g(0, "CIRCLE"); g(8, L(layer)); g(10, f(x)); g(20, f(y)); g(30, 0); g(40, f(r)); },
    text(x, y, h, str, layer, rot = 0, align = 0) {
      if (!str || h <= 0) return;
      g(0, "TEXT"); g(8, L(layer)); g(10, f(x)); g(20, f(y)); g(30, 0); g(40, f(h)); g(1, str.replace(/[\r\n]/g, " "));
      if (rot) g(50, f(rot));
      if (align) { g(72, align); g(11, f(x)); g(21, f(y)); g(31, 0); }
    },
    solid(pts, layer) { g(0, "SOLID"); g(8, L(layer)); [0, 1, 2, 2].forEach((k, i) => { g(10 + i, f(pts[k][0])); g(20 + i, f(pts[k][1])); g(30 + i, 0); }); },
    block(name, build) { const n = ents.length; build(); blocks.push([name, ents.splice(n)]); layers.add("0"); },
    insert(name, x, y, s, layer) { g(0, "INSERT"); g(8, L(layer)); g(2, name); g(10, f(x)); g(20, f(y)); g(30, 0); g(41, +s.toFixed(7)); g(42, +s.toFixed(7)); g(43, 1); },
    toString() {
      const out = [];
      const h = (code, val) => out.push(String(code), String(val));
      h(0, "SECTION"); h(2, "HEADER"); h(9, "$INSUNITS"); h(70, 4); h(0, "ENDSEC");
      h(0, "SECTION"); h(2, "TABLES"); h(0, "TABLE"); h(2, "LTYPE"); h(70, 2);
      h(0, "LTYPE"); h(2, "CONTINUOUS"); h(70, 0); h(3, "Solid"); h(72, 65); h(73, 0); h(40, 0);
      h(0, "LTYPE"); h(2, "DASHED"); h(70, 0); h(3, "Dashed"); h(72, 65); h(73, 2); h(40, 60); h(49, 40); h(49, -20);
      h(0, "ENDTAB");
      h(0, "TABLE"); h(2, "LAYER"); h(70, layers.size);
      [...layers].forEach((n) => { h(0, "LAYER"); h(2, n); h(70, 0); h(62, LAYER_COLORS[n] || 7); h(6, n === "SYMBOL" ? "DASHED" : "CONTINUOUS"); });
      h(0, "ENDTAB"); h(0, "ENDSEC");
      if (blocks.length) {
        h(0, "SECTION"); h(2, "BLOCKS");
        blocks.forEach(([n, body]) => { h(0, "BLOCK"); h(8, "0"); h(2, n); h(70, 0); h(10, 0); h(20, 0); h(30, 0); h(3, n); h(1, ""); out.push(...body); h(0, "ENDBLK"); h(8, "0"); });
        h(0, "ENDSEC");
      }
      return [...out, "0", "SECTION", "2", "ENTITIES", ...ents, "0", "ENDSEC", "0", "EOF"].join("\n");
    },
  };
}

function primsToDxf(doc, prims, X, Y) {
  prims.forEach((p) => {
    const layer = p.dxf || p.layer;
    if (p.t === "rect") {
      doc.line(X(p.x), Y(p.y), X(p.x + p.w), Y(p.y), layer); doc.line(X(p.x + p.w), Y(p.y), X(p.x + p.w), Y(p.y + p.h), layer);
      doc.line(X(p.x + p.w), Y(p.y + p.h), X(p.x), Y(p.y + p.h), layer); doc.line(X(p.x), Y(p.y + p.h), X(p.x), Y(p.y), layer);
    } else if (p.t === "line") doc.line(X(p.x1), Y(p.y1), X(p.x2), Y(p.y2), layer);
    else if (p.t === "circle") doc.circle(X(p.cx), Y(p.cy), p.r, layer);
    else if (p.t === "text") doc.text(X(p.x), Y(p.y), p.h, p.text, layer, p.rot ? -p.rot : 0, p.anchor === "middle" ? 1 : 0);
  });
}

function elevationToDxf(doc, item, offset) {
  const { prims, bounds } = buildPrimitives(item, { dims: true, labels: true });
  primsToDxf(doc, prims, (x) => x + offset, (y) => -y);
  if (!hasPlan(item)) return bounds;
  const plan = planSketch(item), pb = plan.bounds, dy = bounds.y + bounds.h + 400 - pb.y, dx = offset + bounds.x - pb.x;
  primsToDxf(doc, plan.prims, (x) => x + dx, (y) => -(y + dy));
  doc.text(dx + pb.x, -(dy + pb.y - pb.h * 0.04), pb.h * 0.06, `${item.tag} · PLAN (TOP VIEW) · CORNER ANGLES · 1:1`, "PLAN", 0, 0);
  return { ...bounds, w: Math.max(bounds.w, pb.w), h: bounds.h + 400 + pb.h };
}

const LOGO_BLOCK = "AGI_LOGO";

function defineLogo(doc, v) {
  const Y = (y) => v.h - y, t = v.tris;
  doc.block(LOGO_BLOCK, () => {
    for (let i = 0; i < t.length; i += 6) doc.solid([[t[i], Y(t[i + 1])], [t[i + 2], Y(t[i + 3])], [t[i + 4], Y(t[i + 5])]], "LOGO");
    v.outlines.forEach((o) => { for (let i = 0; i < o.length; i += 2) { const j = (i + 2) % o.length; doc.line(o[i], Y(o[i + 1]), o[j], Y(o[j + 1]), "LOGO"); } });
  });
  doc.logo = v;
}

function elevationLogo(doc, b, offset) {
  const v = doc.logo, w = Math.min(1400, Math.max(500, b.w * 0.45)), k = w / v.w, th = w * 0.028, top = b.y + b.h + 300;
  doc.text(offset + b.x, -(top + th), th, "DESIGNED & DETAILED BY", "LOGO");
  doc.insert(LOGO_BLOCK, offset + b.x, -(top + th * 1.8 + v.h * k), k, "LOGO");
  return { ...b, h: b.h + 300 + th * 1.8 + v.h * k };
}

const isBlankFill = (el) => /^(#fff|#ffffff|white)$/i.test(el.getAttribute("fill") || "") && (!el.getAttribute("stroke") || el.getAttribute("stroke") === "none");

export function svgSheetToDxf(doc, svg, ox, oy) {
  const S = parseFloat(svg.dataset.scale) || 1;
  const matrixOf = (el) => {
    let m = new DOMMatrix();
    for (let n = el; n && n !== svg; n = n.parentNode) {
      const t = n.transform?.baseVal?.consolidate?.();
      if (t) { const a = t.matrix; m = new DOMMatrix([a.a, a.b, a.c, a.d, a.e, a.f]).multiply(m); }
    }
    return m;
  };
  const T = (m, x, y) => { const p = m.transformPoint({ x, y }); return [ox + p.x * S, oy - p.y * S]; };
  const num = (el, a) => parseFloat(el.getAttribute(a)) || 0;
  svg.querySelectorAll("rect,line,circle,polygon,polyline,path,text,image[data-logo]").forEach((el) => {
    const m = matrixOf(el), layer = el.dataset.layer || "SHEET", tag = el.tagName.toLowerCase();
    if (tag === "image") {
      const v = doc.logo;
      if (!v) return;
      const k = num(el, "width") / v.src_w, p = T(m, num(el, "x") + v.pad[0] * k, num(el, "y") + (v.pad[1] + v.h) * k);
      doc.insert(LOGO_BLOCK, p[0], p[1], k * Math.hypot(m.a, m.b) * S, "LOGO");
    } else if (tag === "rect") {
      if (isBlankFill(el)) return;
      const x = num(el, "x"), y = num(el, "y"), w = num(el, "width"), h = num(el, "height");
      if (!w || !h) return;
      const c = [T(m, x, y), T(m, x + w, y), T(m, x + w, y + h), T(m, x, y + h)];
      c.forEach((p, i) => { const q = c[(i + 1) % 4]; doc.line(p[0], p[1], q[0], q[1], layer); });
    } else if (tag === "line") {
      const a = T(m, num(el, "x1"), num(el, "y1")), b = T(m, num(el, "x2"), num(el, "y2"));
      doc.line(a[0], a[1], b[0], b[1], layer);
    } else if (tag === "circle") {
      const c = T(m, num(el, "cx"), num(el, "cy"));
      doc.circle(c[0], c[1], num(el, "r") * Math.hypot(m.a, m.b) * S, layer);
    } else if (tag === "polygon" || tag === "polyline") {
      const pts = [...el.points].map((p) => T(m, p.x, p.y));
      const n = tag === "polygon" ? pts.length : pts.length - 1;
      for (let i = 0; i < n; i++) { const q = pts[(i + 1) % pts.length]; doc.line(pts[i][0], pts[i][1], q[0], q[1], layer); }
    } else if (tag === "path") {
      let cur = null;
      ((el.getAttribute("d") || "").match(/[ML][^ML]*/g) || []).forEach((cmd) => {
        const [x, y] = cmd.slice(1).trim().split(/[\s,]+/).map(Number), p = T(m, x, y);
        if (cmd[0] === "L" && cur) doc.line(cur[0], cur[1], p[0], p[1], layer);
        cur = p;
      });
    } else {
      const p = T(m, num(el, "x"), num(el, "y"));
      const h = num(el, "font-size") * Math.hypot(m.a, m.b) * S;
      const rot = -(Math.atan2(m.b, m.a) * 180) / Math.PI;
      const anchor = el.getAttribute("text-anchor");
      doc.text(p[0], p[1], h, el.textContent, layer, Math.round(rot * 100) / 100, anchor === "middle" ? 1 : anchor === "end" ? 2 : 0);
    }
  });
  return { w: 420 * S, h: 297 * S };
}

export function exportDxf(items, sheetSvgs, filename, vectorSheets = [], logo = null) {
  const doc = dxfDoc();
  if (logo) defineLogo(doc, logo);
  let offset = 0, maxH = 0;
  items.forEach((item) => {
    let b = elevationToDxf(doc, item, offset);
    if (logo) b = elevationLogo(doc, b, offset);
    offset += b.w + 500;
    maxH = Math.max(maxH, b.h);
  });
  let sx = 0;
  const sy = -(maxH + 2000);
  sheetSvgs.forEach((svg) => {
    const { w } = svgSheetToDxf(doc, svg, sx, sy);
    sx += w + 1000;
  });
  vectorSheets.forEach(({ tag, name, vec }) => {
    const oy = sy - 4000;
    Object.entries(vec.layers).forEach(([layer, segs]) => segs.forEach(([x1, y1, x2, y2]) => doc.line(sx + x1, oy - y1, sx + x2, oy - y2, `IWIN_${layer}`)));
    doc.text(sx, oy + 8, 5, `${tag} · OFFICIAL IWIN-S SECTION DRAWING · ${name} (A3 as plotted, mm)`, "TITLE");
    sx += vec.w + 40;
  });
  download(new Blob([doc.toString()], { type: "application/dxf" }), filename);
}
